import { CalendarDays } from 'lucide-react';
import { formatChipDate, normalizeDateKey } from '../scheduleStepHelpers';
import type { BookingSlot } from '@/features/campaigns/types/campaign';

type DateStripProps = {
  dates: string[];
  allDatesCount: number;
  showRangeNote: boolean;
  activeDate: string;
  onSelectDate: (date: string) => void;
  bookingSlots: BookingSlot[];
  fullyBookedDates?: string[];
};

function dayStats(bookingSlots: BookingSlot[], dateKey: string) {
  let hours = 0;
  let plays = 0;
  bookingSlots.forEach((s) => {
    if (normalizeDateKey(s.date) !== dateKey) return;
    hours += 1;
    plays += s.repetitions || 1;
  });
  return { hours, plays };
}

/**
 * Campaign date navigator — compact chips on phone, equal grid on tablet+ when few days.
 */
export function DateStrip({
  dates,
  allDatesCount,
  showRangeNote,
  activeDate,
  onSelectDate,
  bookingSlots,
  fullyBookedDates = [],
}: DateStripProps) {
  const fullyBookedSet = new Set(fullyBookedDates.map(normalizeDateKey));
  const activeNormalized = normalizeDateKey(activeDate);
  const daysWithBookings = dates.filter(
    (d) => dayStats(bookingSlots, normalizeDateKey(d)).hours > 0,
  ).length;
  const useEqualGrid = dates.length > 0 && dates.length <= 7;

  return (
    <section
      className="rounded-xl border border-[#E6E8EF] bg-white text-left"
      aria-label="Campaign dates"
    >
      <div className="flex items-center justify-between gap-2 border-b border-[#EEF0F5] px-2.5 py-2 sm:px-3 sm:py-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:gap-2">
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[var(--dash-brand)] sm:h-4 sm:w-4" />
          <h2 className="m-0 text-[12.5px] font-semibold text-[#1F2433] sm:text-[13px]">
            2. Date
          </h2>
          <span className="rounded-md bg-[#F3F4F8] px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums text-[#6B7289] sm:text-[11px]">
            {daysWithBookings}/{dates.length} booked
          </span>
        </div>
        <span className="hidden text-[11px] font-medium text-[#9CA3AF] sm:inline">
          {showRangeNote
            ? `${dates.length} of ${allDatesCount} days`
            : `${dates.length} day${dates.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {showRangeNote ? (
        <p className="mx-2.5 mt-2 mb-0 rounded-lg border border-[#DBEAFE] bg-[#F5F9FF] px-2.5 py-1.5 text-[11px] leading-relaxed text-[#1E40AF] sm:mx-3">
          {allDatesCount} campaign days. Keep Sync on to apply this plan across
          every date, or book each day individually.
        </p>
      ) : null}

      <div
        role="tablist"
        aria-label="Select date"
        className={`gap-1.5 p-1.5 sm:p-2 ${
          useEqualGrid
            ? 'flex overflow-x-auto overscroll-x-contain [scrollbar-width:none] sm:grid sm:overflow-visible [&::-webkit-scrollbar]:hidden'
            : 'flex overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        }`}
        style={
          useEqualGrid
            ? { gridTemplateColumns: `repeat(${dates.length}, minmax(0, 1fr))` }
            : undefined
        }
      >
        {dates.map((d) => {
          const normD = normalizeDateKey(d);
          const { hours, plays } = dayStats(bookingSlots, normD);
          const active = activeNormalized === normD;
          const isFullyBooked = fullyBookedSet.has(normD);
          const label = formatChipDate(d);
          const hasBookings = hours > 0;

          return (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelectDate(d)}
              className={`flex min-h-[48px] flex-col items-stretch rounded-xl border px-2 py-1.5 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--dash-brand)] sm:min-h-0 sm:px-2.5 sm:py-2 ${
                useEqualGrid
                  ? 'w-[72px] shrink-0 sm:min-w-0 sm:w-auto'
                  : 'w-[72px] shrink-0 sm:min-w-[100px] sm:w-auto'
              } ${
                active
                  ? 'border-[var(--dash-brand)] bg-[var(--dash-brand-soft)] shadow-[0_0_0_1px_var(--dash-brand)]'
                  : isFullyBooked
                    ? 'border-[#F1E8E8] bg-[#FCF8F8]'
                    : hasBookings
                      ? 'border-[var(--dash-brand)]/20 bg-white hover:border-[var(--dash-brand)]/45'
                      : 'border-[#E6E8EF] bg-[#F8F9FB] hover:border-[#D0D4DE] hover:bg-white'
              }`}
            >
              <div className="flex items-center justify-between gap-1">
                <span
                  className={`text-[9px] font-bold tracking-wide uppercase sm:text-[10px] ${
                    active ? 'text-[var(--dash-brand)]' : 'text-[#9CA3AF]'
                  }`}
                >
                  {label.weekday}
                </span>
                {active ? (
                  <span className="hidden rounded bg-[var(--dash-brand)] px-1 py-px text-[8px] font-bold tracking-wide text-white uppercase sm:inline">
                    Active
                  </span>
                ) : null}
              </div>

              <span className="mt-0.5 text-[13px] font-semibold leading-tight text-[#1F2433] sm:text-[14px]">
                {label.dayMonth}
              </span>

              <span className="mt-1 min-h-[14px] sm:mt-2 sm:min-h-[20px]">
                {isFullyBooked ? (
                  <span className="inline-flex rounded-md bg-rose-50 px-1 py-0.5 text-[9px] font-semibold text-rose-700 sm:px-1.5 sm:text-[10px]">
                    Full
                  </span>
                ) : hasBookings ? (
                  <span
                    className={`inline-flex items-center gap-0.5 rounded-md px-1 py-0.5 text-[9px] font-semibold sm:gap-1 sm:px-1.5 sm:text-[10px] ${
                      active
                        ? 'bg-white text-[var(--dash-brand)]'
                        : 'bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]'
                    }`}
                  >
                    <span className="tabular-nums">{hours}</span>
                    <span className="hidden sm:inline">
                      {hours === 1 ? 'hr' : 'hrs'}
                    </span>
                    <span className="opacity-40">·</span>
                    <span className="tabular-nums">{plays}</span>
                    <span className="hidden sm:inline">plays</span>
                  </span>
                ) : (
                  <span className="text-[9px] font-medium text-[#9CA3AF] sm:text-[10px]">
                    <span className="sm:hidden">—</span>
                    <span className="hidden sm:inline">No slots yet</span>
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
